
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

const VOLUME_STORAGE_KEY = 'compliance_command_audio_volumes';
const MIN_AUDIBLE = 0.01;
const AMBIENT_BASE_GAIN = 0.015;
const MIN_GEMINI_SAMPLES = 480; // ~20ms at 24kHz — skip shorter buffers (clicks/pops)

type StoredVolumes = { master: number; sfx: number; ambient: number };

function loadStoredVolumes(): StoredVolumes {
  if (typeof localStorage === 'undefined') {
    return { master: 0.5, sfx: 1.0, ambient: 0 };
  }
  try {
    const raw = localStorage.getItem(VOLUME_STORAGE_KEY);
    if (!raw) return { master: 0.5, sfx: 1.0, ambient: 0 };
    const parsed = JSON.parse(raw) as Partial<StoredVolumes>;
    return {
      master: clampVolume(parsed.master ?? 0.5),
      sfx: clampVolume(parsed.sfx ?? 1.0),
      ambient: clampVolume(parsed.ambient ?? 0),
    };
  } catch {
    return { master: 0.5, sfx: 1.0, ambient: 0 };
  }
}

function clampVolume(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

function persistVolumes(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(
      VOLUME_STORAGE_KEY,
      JSON.stringify({ master: masterVolume, sfx: sfxVolume, ambient: ambientVolume })
    );
  } catch {
    /* ignore quota / private mode */
  }
}

// Singleton Audio Context
let audioCtx: AudioContext | null = null;
let ambientOsc: OscillatorNode | null = null;
let ambientGain: GainNode | null = null;
let ambientFilter: BiquadFilterNode | null = null;
let activeGeminiSource: AudioBufferSourceNode | null = null;
let activeGeminiGain: GainNode | null = null;

const stored = loadStoredVolumes();
let masterVolume = stored.master;
let sfxVolume = stored.sfx;
let ambientVolume = stored.ambient;

function effectiveSfxVolume(): number {
  return masterVolume * sfxVolume;
}

function effectiveAmbientGain(): number {
  return AMBIENT_BASE_GAIN * ambientVolume * masterVolume;
}

function isSfxMuted(): boolean {
  return effectiveSfxVolume() <= MIN_AUDIBLE;
}

function isAmbientMuted(): boolean {
  return effectiveAmbientGain() <= MIN_AUDIBLE;
}

export const getVolumes = (): StoredVolumes => ({
  master: masterVolume,
  sfx: sfxVolume,
  ambient: ambientVolume,
});

export const setVolumes = (master: number, sfx: number, ambient: number) => {
  masterVolume = clampVolume(master);
  sfxVolume = clampVolume(sfx);
  ambientVolume = clampVolume(ambient);
  persistVolumes();

  if (isAmbientMuted()) {
    stopAmbientNoise();
    return;
  }

  if (!ambientOsc) {
    startAmbientNoise();
    return;
  }

  if (audioCtx && ambientGain) {
    ambientGain.gain.setTargetAtTime(effectiveAmbientGain(), audioCtx.currentTime, 0.1);
  }
};

export const initAudio = () => {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  if (audioCtx.state === 'suspended') {
    void audioCtx.resume();
  }
  return audioCtx;
};

export const startAmbientNoise = () => {
  if (isAmbientMuted()) return;

  const ctx = initAudio();
  if (ambientOsc) {
    if (ambientGain) {
      ambientGain.gain.setTargetAtTime(effectiveAmbientGain(), ctx.currentTime, 0.1);
    }
    return;
  }

  ambientOsc = ctx.createOscillator();
  ambientGain = ctx.createGain();
  ambientFilter = ctx.createBiquadFilter();

  ambientOsc.type = 'sawtooth';
  ambientOsc.frequency.setValueAtTime(50, ctx.currentTime);

  ambientFilter.type = 'lowpass';
  ambientFilter.frequency.setValueAtTime(120, ctx.currentTime);

  ambientGain.gain.setValueAtTime(0, ctx.currentTime);
  ambientGain.gain.linearRampToValueAtTime(effectiveAmbientGain(), ctx.currentTime + 0.15);

  ambientOsc.connect(ambientFilter);
  ambientFilter.connect(ambientGain);
  ambientGain.connect(ctx.destination);

  ambientOsc.start();
};

export const stopAmbientNoise = () => {
  if (ambientOsc) {
    try {
      ambientOsc.stop();
      ambientOsc.disconnect();
    } catch {
      /* already stopped */
    }
    ambientOsc = null;
  }
  if (ambientFilter) {
    try {
      ambientFilter.disconnect();
    } catch {
      /* ignore */
    }
    ambientFilter = null;
  }
  if (ambientGain) {
    try {
      ambientGain.disconnect();
    } catch {
      /* ignore */
    }
    ambientGain = null;
  }
};

export type SoundType = 'click' | 'hover' | 'build' | 'destroy' | 'money' | 'error' | 'success' | 'alert' | 'typewriter';

function scheduleOsc(
  ctx: AudioContext,
  osc: OscillatorNode,
  gain: GainNode,
  startAt: number,
  stopAt: number
): void {
  osc.start(startAt);
  osc.stop(stopAt);
  gain.gain.setValueAtTime(0, stopAt);
}

export const playSfx = (type: SoundType) => {
  const vol = effectiveSfxVolume();
  if (vol <= MIN_AUDIBLE) return;

  const ctx = initAudio();
  const t = ctx.currentTime;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  osc.connect(gain);
  gain.connect(ctx.destination);

  switch (type) {
    case 'click':
      osc.type = 'sine';
      osc.frequency.setValueAtTime(600, t);
      osc.frequency.exponentialRampToValueAtTime(300, t + 0.1);
      gain.gain.setValueAtTime(0.1 * vol, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.1);
      scheduleOsc(ctx, osc, gain, t, t + 0.1);
      break;

    case 'hover':
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(400, t);
      gain.gain.setValueAtTime(0.02 * vol, t);
      gain.gain.linearRampToValueAtTime(0, t + 0.05);
      scheduleOsc(ctx, osc, gain, t, t + 0.05);
      break;

    case 'build': {
      osc.type = 'square';
      osc.frequency.setValueAtTime(100, t);
      osc.frequency.exponentialRampToValueAtTime(40, t + 0.2);
      gain.gain.setValueAtTime(0.2 * vol, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.3);

      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = 'sawtooth';
      osc2.frequency.setValueAtTime(50, t);
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      gain2.gain.setValueAtTime(0.1 * vol, t);
      gain2.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
      scheduleOsc(ctx, osc2, gain2, t, t + 0.2);

      scheduleOsc(ctx, osc, gain, t, t + 0.3);
      break;
    }

    case 'destroy':
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(200, t);
      osc.frequency.linearRampToValueAtTime(50, t + 0.2);
      gain.gain.setValueAtTime(0.2 * vol, t);
      gain.gain.linearRampToValueAtTime(0, t + 0.2);
      scheduleOsc(ctx, osc, gain, t, t + 0.2);
      break;

    case 'money':
      osc.type = 'sine';
      osc.frequency.setValueAtTime(1200, t);
      osc.frequency.setValueAtTime(1800, t + 0.1);
      gain.gain.setValueAtTime(0.05 * vol, t);
      gain.gain.linearRampToValueAtTime(0, t + 0.3);
      scheduleOsc(ctx, osc, gain, t, t + 0.3);
      break;

    case 'error':
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(150, t);
      osc.frequency.linearRampToValueAtTime(100, t + 0.2);
      gain.gain.setValueAtTime(0.1 * vol, t);
      gain.gain.linearRampToValueAtTime(0, t + 0.2);
      scheduleOsc(ctx, osc, gain, t, t + 0.2);
      break;

    case 'success':
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(440, t);
      osc.frequency.setValueAtTime(880, t + 0.15);
      gain.gain.setValueAtTime(0.1 * vol, t);
      gain.gain.linearRampToValueAtTime(0, t + 0.4);
      scheduleOsc(ctx, osc, gain, t, t + 0.4);
      break;

    case 'alert':
      osc.type = 'square';
      osc.frequency.setValueAtTime(600, t);
      osc.frequency.setValueAtTime(800, t + 0.1);
      osc.frequency.setValueAtTime(600, t + 0.2);
      gain.gain.setValueAtTime(0.1 * vol, t);
      gain.gain.linearRampToValueAtTime(0, t + 0.5);
      scheduleOsc(ctx, osc, gain, t, t + 0.5);
      break;

    case 'typewriter':
      osc.type = 'square';
      osc.frequency.setValueAtTime(800, t);
      gain.gain.setValueAtTime(0.03 * vol, t);
      gain.gain.linearRampToValueAtTime(0, t + 0.02);
      scheduleOsc(ctx, osc, gain, t, t + 0.02);
      break;

    default:
      try {
        osc.disconnect();
        gain.disconnect();
      } catch {
        /* ignore */
      }
      break;
  }
};

export const stopGeminiAudio = (): void => {
  if (activeGeminiSource) {
    try {
      activeGeminiSource.onended = null;
      activeGeminiSource.stop();
      activeGeminiSource.disconnect();
    } catch {
      /* already stopped */
    }
    activeGeminiSource = null;
  }
  if (activeGeminiGain) {
    try {
      activeGeminiGain.disconnect();
    } catch {
      /* ignore */
    }
    activeGeminiGain = null;
  }
};

export const playGeminiAudio = async (base64String: string) => {
  if (!base64String?.trim() || masterVolume <= MIN_AUDIBLE) return null;

  const ctx = initAudio();
  stopGeminiAudio();

  try {
    const binaryString = atob(base64String);
    const len = binaryString.length;
    const safeLen = len % 2 === 0 ? len : len - 1;
    if (safeLen < 4) return null;

    const bytes = new Uint8Array(safeLen);
    for (let i = 0; i < safeLen; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const sampleRate = 24000;
    const int16Array = new Int16Array(bytes.buffer);
    if (int16Array.length < MIN_GEMINI_SAMPLES) return null;

    const audioBuffer = ctx.createBuffer(1, int16Array.length, sampleRate);
    const channelData = audioBuffer.getChannelData(0);

    for (let i = 0; i < int16Array.length; i++) {
      channelData[i] = int16Array[i] / 32768.0;
    }

    const source = ctx.createBufferSource();
    const gain = ctx.createGain();
    gain.gain.value = masterVolume;

    source.buffer = audioBuffer;
    source.connect(gain);
    gain.connect(ctx.destination);
    activeGeminiSource = source;
    activeGeminiGain = gain;
    source.onended = () => {
      if (activeGeminiSource === source) {
        activeGeminiSource = null;
        activeGeminiGain = null;
      }
    };
    source.start(0);
    return source;
  } catch (e) {
    console.error('Audio playback error', e);
    return null;
  }
};